import { ref, useRoute, onMounted, useRouter } from '#imports'

const isAmazonState = ref(false)
const AMAZON_STATE_KEY = 'zenni_is_amazon'

export const useAmazonState = () => {
  const route = useRoute()
  const router = useRouter()

  /**
   * Check if the current route has the Amazon prefix
   */
  const checkIsAmazon = () => {
    const path = route.path
    const isAmazonRoute = path.startsWith('/amazon')

    if (isAmazonRoute) {
      // If the route has the Amazon prefix, set the state to true and save to localStorage
      isAmazonState.value = true
      if (process.client) {
        localStorage.setItem(AMAZON_STATE_KEY, 'true')
        // Set cookie with path='/' to ensure it's accessible from all routes
        document.cookie = `${AMAZON_STATE_KEY}=true; path=/; max-age=86400; SameSite=Lax`
      }
    } else if (process.client) {
      // If not on Amazon route but on client, check localStorage for the saved state
      const savedState = localStorage.getItem(AMAZON_STATE_KEY)
      isAmazonState.value = savedState === 'true'
    }

    return isAmazonState.value
  }

  /**
   * Get the current Amazon state
   */
  const isAmazon = () => {
    // If we're on the client, check localStorage first
    if (process.client) {
      const savedState = localStorage.getItem(AMAZON_STATE_KEY)
      if (savedState !== null) {
        isAmazonState.value = savedState === 'true'
      }
    }
    return isAmazonState.value
  }

  /**
   * Set the Amazon state
   */
  const setIsAmazon = (value: boolean) => {
    isAmazonState.value = value
    if (process.client) {
      localStorage.setItem(AMAZON_STATE_KEY, value ? 'true' : 'false')
      // Set cookie with path='/' to ensure it's accessible from all routes
      document.cookie = `${AMAZON_STATE_KEY}=${
        value ? 'true' : 'false'
      }; path=/; max-age=86400; SameSite=Lax`
    }
  }

  /**
   * Clear the Amazon state
   */
  const clearAmazonState = () => {
    isAmazonState.value = false
    if (process.client) {
      localStorage.removeItem(AMAZON_STATE_KEY)

      // Clear all related cookies by setting expiration in the past
      // Clear the root path cookies
      document.cookie = `${AMAZON_STATE_KEY}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`

      // Also clear the /amazon path cookie
      document.cookie = `magento-store=; path=/amazon; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`
    }
  }

  /**
   * Helper function to get a cookie value by name
   */
  const getCookie = (name: string): string | null => {
    if (!process.client) return null

    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1)
      }
    }
    return null
  }

  /**
   * Helper function to navigate to a route with Amazon prefix if in Amazon context
   * This is a utility function that can be used directly if needed, but
   * the global middleware should handle most cases automatically
   */
  const navigateWithAmazonContext = (path: string, options = {}) => {
    if (
      isAmazonState.value &&
      !path.startsWith('/amazon') &&
      !path.startsWith('http')
    ) {
      // Add the /amazon prefix to the path
      const amazonPath = `/amazon${path.startsWith('/') ? path : `/${path}`}`
      return router.push({ path: amazonPath, ...options })
    }

    // Otherwise, navigate normally
    return router.push({ path, ...options })
  }

  /**
   * Initialize the Amazon state from localStorage
   */
  const initAmazonState = () => {
    if (process.client) {
      const savedState = localStorage.getItem(AMAZON_STATE_KEY)
      if (savedState !== null) {
        isAmazonState.value = savedState === 'true'

        // Ensure cookie is set with the current state
        document.cookie = `${AMAZON_STATE_KEY}=${savedState}; path=/; max-age=86400; SameSite=Lax`

        // If in Amazon context, ensure we have the magento-store cookie duplicated
      }
    }
  }

  // Initialize the state from localStorage on mount, but only when used in a component
  onMounted(initAmazonState)

  return {
    isAmazon,
    setIsAmazon,
    checkIsAmazon,
    clearAmazonState,
    navigateWithAmazonContext,
  }
}
